import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import api from '../../services/api';

const AddEquipmentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [equipmentTypes, setEquipmentTypes] = useState<any[]>([]);
  const [subscription, setSubscription] = useState<any>(null);
  const [formData, setFormData] = useState({
    equipment_type_id: '',
    brand: '',
    model: '',
    serial_number: '',
    purchase_date: null as Date | null,
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const serviceUserData = localStorage.getItem('serviceUserData');
      if (!serviceUserData) {
        navigate('/app/reminders/dashboard');
        return;
      }
      
      const userData = JSON.parse(serviceUserData);
      const serviceUserId = userData.id;

      const [typesRes, subRes] = await Promise.all([
        api.get('/reminders/equipment-types'),
        api.get(`/reminders/subscription/${serviceUserId}`),
      ]);

      setEquipmentTypes(typesRes.data);
      setSubscription(subRes.data);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(t('reminders.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      equipment_type_id: event.target.value,
    });
  };

  const handleDateChange = (value: any) => {
    const date = value instanceof Date ? value : value?.toDate ? value.toDate() : null;
    setFormData({
      ...formData,
      purchase_date: date,
    });
  };

  // Check if equipment type needs brand/model
  const isDocumentType = () => {
    const selectedType = equipmentTypes.find(t => t.id === formData.equipment_type_id);
    return selectedType && (
      selectedType.name === 'Pilot License' || 
      selectedType.name === 'Medical Certificate'
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For documents (license/medical), only equipment_type_id is required
    // For physical equipment, brand and model are required
    if (!formData.equipment_type_id) {
      setError(t('common.fillRequiredFields'));
      return;
    }

    if (!isDocumentType() && (!formData.brand || !formData.model)) {
      setError(t('common.fillRequiredFields'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const requestData = {
        subscription_id: subscription.id,
        equipment_type_id: formData.equipment_type_id,
        brand: formData.brand,
        model: formData.model,
        serial_number: formData.serial_number || null,
        purchase_date: formData.purchase_date?.toISOString() || null,
        notes: formData.notes || null,
      };

      await api.post('/reminders/equipment', requestData);
      // Navigate back to reminders dashboard
      navigate('/app/reminders/dashboard');
    } catch (err: any) {
      console.error('Error saving equipment:', err);
      setError(t('reminders.errors.equipmentFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AppBar position="fixed" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('reminders.addEquipment')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pt: 10, pb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <BuildIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h5">
              {t('reminders.addEquipment')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>{t('reminders.equipment.type')}</InputLabel>
                <Select
                  value={formData.equipment_type_id}
                  onChange={handleSelectChange}
                  label={t('reminders.equipment.type')}
                >
                  {equipmentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!isDocumentType() ? (
                <>
                  <TextField
                    fullWidth
                    required
                    label={t('reminders.equipment.brand')}
                    value={formData.brand}
                    onChange={handleInputChange('brand')}
                  />

                  <TextField
                    fullWidth
                    required
                    label={t('reminders.equipment.model')}
                    value={formData.model}
                    onChange={handleInputChange('model')}
                  />
                </>
              ) : (
                <TextField
                  fullWidth
                  label={t('reminders.equipment.documentNumber')}
                  value={formData.serial_number}
                  onChange={handleInputChange('serial_number')}
                  placeholder={isDocumentType() && formData.equipment_type_id ? 
                    (equipmentTypes.find(t => t.id === formData.equipment_type_id)?.name === 'Pilot License' ? 
                      t('reminders.equipment.licenseNumberPlaceholder') : 
                      t('reminders.equipment.certificateNumberPlaceholder')) : 
                    ''}
                />
              )}

              {!isDocumentType() && (
                <TextField
                  fullWidth
                  label={t('reminders.equipment.serialNumber')}
                  value={formData.serial_number}
                  onChange={handleInputChange('serial_number')}
                />
              )}

              <DatePicker
                label={isDocumentType() ? 
                  t('reminders.equipment.expiryDate') : 
                  t('reminders.equipment.purchaseDate')}
                value={formData.purchase_date}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('reminders.equipment.notes')}
                value={formData.notes}
                onChange={handleInputChange('notes')}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={saving}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : t('common.save')}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default AddEquipmentPage;